<script setup lang="ts">
  import { computed } from 'vue';
  import { IconButton, Tooltip } from '@/shared/ui';
  import type { MemberTrainingStatus, TrainingCollaboratorListItem } from '@/entities/Training';
  import TrainingStatusBadge from '../TrainingStatusBadge.vue';
  import TrainingCollaboratorStatusMenu from '../TrainingCollaboratorStatusMenu.vue';
  import TrainingCollaboratorPositions from './TrainingCollaboratorPositions.vue';
  import { collaboratorTableCellModifierClass } from '../../model/useTrainingCollaboratorsTable';
  import UploadActionIcon from '@/shared/assets/icons/UploadActionIcon.svg';
  import CertificateAddIcon from '@/shared/assets/icons/CertificateAddIcon.svg';
  import CertificateIcon from '@/shared/assets/icons/CertificateIcon.svg';

  const props = withDefaults(
    defineProps<{
      collaborator: TrainingCollaboratorListItem;
      isChangingStatus?: (id: number) => boolean;
      isGenerating?: boolean;
    }>(),
    { isChangingStatus: () => false, isGenerating: false }
  );

  const emit = defineEmits<{
    'change-status': [id: number, status: MemberTrainingStatus];
    'upload-certificate': [collaborator: TrainingCollaboratorListItem];
    'view-certificate': [collaborator: TrainingCollaboratorListItem];
    'generate-certificate': [collaborator: TrainingCollaboratorListItem];
  }>();

  const isCompleted = computed(() => props.collaborator.status === 'COMPLETED');
</script>

<template>
  <tr
    data-testid="training-collaborator-row"
    class="border-b border-gray-02 last:border-b-0"
  >
    <td
      class="pl-6 py-4.5 text-sm text-gray-07 text-left"
      :class="collaboratorTableCellModifierClass(0)"
    >
      {{ collaborator.name }}
    </td>
    <td
      class="pl-6 py-4.5 text-sm text-gray-07 text-left"
      :class="collaboratorTableCellModifierClass(1)"
    >
      {{ collaborator.registrationNumber }}
    </td>
    <td
      class="pl-6 py-4.5 text-sm text-gray-07 text-left"
      :class="collaboratorTableCellModifierClass(2)"
    >
      <TrainingCollaboratorPositions :positions="collaborator.positions" />
    </td>
    <td
      class="pl-6 py-4.5 text-left"
      :class="collaboratorTableCellModifierClass(3)"
    >
      <TrainingStatusBadge :status="collaborator.status" />
    </td>
    <td
      class="pl-6 py-4.5"
      :class="collaboratorTableCellModifierClass(4)"
    >
      <div class="flex items-center gap-1 text-gray-xx-02">
        <TrainingCollaboratorStatusMenu
          :status="collaborator.status"
          :loading="isChangingStatus(collaborator.id)"
          @change="(status) => emit('change-status', collaborator.id, status)"
        />
        <Tooltip
          content="Upload de certificado"
          class="flex items-center"
        >
          <IconButton
            :src="UploadActionIcon"
            alt="Upload de certificado"
            class="w-6 h-6"
            :disabled="!isCompleted"
            @click="emit('upload-certificate', collaborator)"
          />
        </Tooltip>
        <Tooltip
          content="Visualizar Certificado"
          class="flex items-center"
        >
          <IconButton
            :src="CertificateIcon"
            alt="Visualizar Certificado"
            class="w-6 h-6"
            :disabled="!collaborator.hasCertificate"
            @click="emit('view-certificate', collaborator)"
          />
        </Tooltip>
        <Tooltip
          content="Gerar Certificado"
          class="flex items-center"
        >
          <IconButton
            :src="CertificateAddIcon"
            alt="Gerar Certificado"
            class="w-6 h-6"
            :disabled="!isCompleted"
            :loading="isGenerating"
            @click="emit('generate-certificate', collaborator)"
          />
        </Tooltip>
      </div>
    </td>
  </tr>
</template>
